import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Cloud, CloudOff, AlertTriangle, X, CheckCircle2 } from 'lucide-react';
import { Sheet } from './Sheet';
import { Symptom } from '../lib/types';
import { localStore } from '../lib/storage';
import { getCustomSymptomConfigs } from '../lib/symptomCategories';
import { findScoreRepairs, SymptomRepair, CloudState } from '../lib/scoreRepair';

interface ScoreRepairPreviewSheetProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onApply?: (repairs: SymptomRepair[]) => Promise<{ repairedCount: number; cloudPendingCount: number }>;
}

type ApplyState =
  | { step: 'idle' }
  | { step: 'confirm' }
  | { step: 'applying' }
  | { step: 'done'; repairedCount: number; cloudPendingCount: number }
  | { step: 'error'; message: string };

type PreviewState =
  | { status: 'loading' }
  | {
      status: 'ready';
      repairs: SymptomRepair[];
      checkedCount: number;
      backup: { createdAt: string; count: number } | null;
      cloudReachable: boolean;
    };

const CLOUD_LABEL: Record<CloudState, { text: string; className: string }> = {
  'matches-original': { text: 'Cloud has original', className: 'text-emerald-600 dark:text-emerald-400' },
  inflated: { text: 'Cloud also inflated', className: 'text-amber-600 dark:text-amber-400' },
  missing: { text: 'Not in cloud', className: 'text-slate-400 dark:text-zinc-500' },
  unknown: { text: 'Cloud not checked', className: 'text-slate-400 dark:text-zinc-500' },
};

const formatWhen = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/**
 * Preview of the symptom score repair. Opening it makes a write-once safety backup;
 * scores only change when the user confirms Apply.
 */
export const ScoreRepairPreviewSheet: React.FC<ScoreRepairPreviewSheetProps> = ({ isOpen, onClose, userId, onApply }) => {
  const [state, setState] = useState<PreviewState>({ status: 'loading' });
  const [apply, setApply] = useState<ApplyState>({ step: 'idle' });

  const runCheck = useCallback(async (isCancelled: () => boolean = () => false) => {
    setState({ status: 'loading' });
    const backup = await localStore.ensureSymptomRepairBackup(userId);
    const local = localStore.getRawSymptoms(userId);
    const definitions = getCustomSymptomConfigs(userId);

    let cloud: Symptom[] | null = null;
    try {
      const res = await fetch('/api/symptoms', { headers: { 'X-User-Id': userId } });
      if (res.ok) {
        const body = await res.json();
        if (Array.isArray(body)) cloud = body;
      }
    } catch {
      cloud = null;
    }

    if (isCancelled()) return;
    setState({
      status: 'ready',
      repairs: findScoreRepairs(local, definitions, cloud),
      checkedCount: local.length,
      backup,
      cloudReachable: cloud !== null,
    });
  }, [userId]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setApply({ step: 'idle' });
    runCheck(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [isOpen, runCheck]);

  const handleApply = async () => {
    if (state.status !== 'ready' || !onApply) return;
    setApply({ step: 'applying' });
    try {
      const result = await onApply(state.repairs);
      setApply({ step: 'done', ...result });
      await runCheck();
    } catch (e) {
      setApply({ step: 'error', message: e instanceof Error ? e.message : 'Repair failed.' });
    }
  };

  const canApply = state.status === 'ready' && state.repairs.length > 0 && Boolean(state.backup) && Boolean(onApply);

  const changeCount = state.status === 'ready' ? state.repairs.reduce((n, r) => n + r.changes.length, 0) : 0;

  return (
    <Sheet isOpen={isOpen} onClose={onClose} ariaLabel="Symptom score check">
      <div className="px-6 py-2.5 flex items-center justify-between border-b border-slate-200/60 dark:border-zinc-800 shrink-0">
        <h2 className="text-lg font-bold tracking-tight">Symptom score check</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-8 h-8 rounded-full bg-slate-200/70 dark:bg-zinc-800 flex items-center justify-center text-slate-700 dark:text-zinc-300"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-6 py-4 space-y-4 overflow-y-auto overscroll-contain min-h-0">
        {state.status === 'loading' ? (
          <p className="text-sm text-slate-500 dark:text-zinc-400">Checking your check-ins…</p>
        ) : (
          <>
            <div className="p-4 rounded-2xl bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 space-y-2">
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                {state.repairs.length === 0
                  ? `All ${state.checkedCount} check-ins match what you entered.`
                  : `${state.repairs.length} of ${state.checkedCount} check-ins have ${changeCount} score${changeCount === 1 ? '' : 's'} that differ from what you entered.`}
              </p>
              <p className="text-[13px] leading-snug text-slate-500 dark:text-zinc-400">
                An older version of the app read some 0–10 scores as the old 1–5 scale and saved them higher (2 became 4, 3
                became 6). The values you tapped are still in each check-in’s text, which is what this check compares against.
              </p>
            </div>

            <div className="flex items-start gap-2.5 text-[13px] leading-snug">
              <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <span className="text-slate-600 dark:text-zinc-300">
                {state.backup
                  ? `Safety backup of ${state.backup.count} check-ins saved on this device (${formatWhen(state.backup.createdAt)}). It is never overwritten.`
                  : 'Could not save a safety backup on this device. Nothing will be repaired without one.'}
              </span>
            </div>

            <div className="flex items-start gap-2.5 text-[13px] leading-snug">
              {state.cloudReachable ? (
                <Cloud className="w-4 h-4 mt-0.5 shrink-0 text-sky-600 dark:text-sky-400" />
              ) : (
                <CloudOff className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
              )}
              <span className="text-slate-600 dark:text-zinc-300">
                {state.cloudReachable
                  ? 'Compared with your cloud copy too.'
                  : 'Cloud copy could not be reached, so only this device was checked.'}
              </span>
            </div>

            {state.repairs.length > 0 && (
              <ul className="rounded-2xl bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 divide-y divide-slate-100 dark:divide-zinc-800">
                {state.repairs.map((r) => (
                  <li key={r.id} className="px-4 py-3 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-semibold text-slate-900 dark:text-white">{formatWhen(r.timestamp_iso)}</span>
                      {state.cloudReachable && (
                        <span className={`text-xs ${CLOUD_LABEL[r.cloud].className}`}>{CLOUD_LABEL[r.cloud].text}</span>
                      )}
                    </div>
                    {r.changes.map((c) => (
                      <div key={c.symptomId} className="flex items-center justify-between text-[13px]">
                        <span className="text-slate-600 dark:text-zinc-300">{c.label}</span>
                        <span className="tabular-nums">
                          <span className="text-slate-400 line-through">{c.stored}</span>
                          <span className="mx-1.5 text-slate-400">→</span>
                          <span className="font-semibold text-slate-900 dark:text-white">{c.original}</span>
                          <span className="text-slate-400">/10</span>
                        </span>
                      </div>
                    ))}
                  </li>
                ))}
              </ul>
            )}

            {apply.step === 'done' && (
              <div className="flex items-start gap-2.5 text-[13px] leading-snug p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/70 dark:border-emerald-800/50">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <span className="text-emerald-900 dark:text-emerald-200">
                  Restored {apply.repairedCount} check-in{apply.repairedCount === 1 ? '' : 's'} on this device.{' '}
                  {apply.cloudPendingCount === 0
                    ? 'Cloud copy updated.'
                    : `${apply.cloudPendingCount} cloud update${apply.cloudPendingCount === 1 ? ' is' : 's are'} queued and will sync when you're online.`}
                </span>
              </div>
            )}

            {apply.step === 'error' && (
              <div className="flex items-start gap-2.5 text-[13px] leading-snug p-3 rounded-2xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200/70 dark:border-rose-800/50">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-rose-600 dark:text-rose-400" />
                <span className="text-rose-900 dark:text-rose-200">{apply.message} Nothing was changed.</span>
              </div>
            )}

            {state.repairs.length > 0 && (
              <div className="flex items-start gap-2.5 text-[13px] leading-snug p-3 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200/70 dark:border-amber-800/50">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <span className="text-amber-900 dark:text-amber-200">
                  Nothing changes until you tap Restore. Check-ins you edited after saving rewrote their text with the higher
                  value, so they can’t be detected here.
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {canApply && (
        <div className="px-6 pt-3 pb-4 border-t border-slate-200/60 dark:border-zinc-800 shrink-0 space-y-2">
          {apply.step === 'confirm' ? (
            <>
              <p className="text-[13px] text-center text-slate-600 dark:text-zinc-300">
                Restore {state.status === 'ready' ? state.repairs.length : 0} check-ins on this phone and in the cloud?
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setApply({ step: 'idle' })}
                  className="flex-1 py-3 rounded-2xl border border-slate-200 dark:border-zinc-800 text-sm font-semibold text-slate-600 dark:text-zinc-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  className="flex-1 py-3 rounded-2xl bg-slate-950 dark:bg-white text-white dark:text-black text-sm font-semibold active:scale-[0.98] transition-transform"
                >
                  Restore
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              disabled={apply.step === 'applying'}
              onClick={() => setApply({ step: 'confirm' })}
              className="w-full py-3 rounded-2xl bg-slate-950 dark:bg-white text-white dark:text-black text-sm font-semibold active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              {apply.step === 'applying' ? 'Restoring…' : 'Restore original scores'}
            </button>
          )}
        </div>
      )}
    </Sheet>
  );
};
